import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ICreditRatingData } from '../../core/models/creditRating/creditRatingResponse';
import {
  BorrowingsKeys,
  BsCompareUlbs,
  BsIsData,
  ExploreSectionResponse,
  IFinancialIndicatorRes,
  IFinancialIndicatorsChart,
  IMoneyInfoRes,
  ISlb,
  MarketReadinessResponse
} from '../../core/models/interfaces';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  constructor(private http: HttpClient) { }

  // Money info cards: tax rev, own rev, grants, tot rev, tot exp, bs size.
  getMoneyInfo(year: string = '', stateId: string = '', ulbId: string = '') {
    let params = new HttpParams();
    if (year) params = params.set('year', year);
    if (stateId) params = params.set('stateId', stateId);
    if (ulbId) params = params.set('ulbId', ulbId);

    return this.http.get<IMoneyInfoRes>(`${environment.api.url}dashboard/financial-info/get-data`, { params });
  }
  downloadMarketExcel(ulbId: string, year: string) {
    let params = new HttpParams();

    if (ulbId) params = params.set('ulbId', ulbId);
    if (year) params = params.set('year', year);

    return this.http.get(`${environment.api.url}ledger/downloadMarketDashboardExcel`, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }
  // City page: balance sheet and income statement table.
  getBsIsData(selectedUlb: string, ulbIds: string[], btnKey: string = 'incomeStatement', years: string[]) {
    let params = new HttpParams();

    params = params.set('btnKey', btnKey);
    params = params.set('selectedUlb', selectedUlb);
    ulbIds.forEach(id => { params = params.append('ulbIds', id); });
    years.forEach(year => { params = params.append('years', year); });

    return this.http.get<{ data: BsIsData[]; population: number, ulbsData: BsCompareUlbs }>(
      `${environment.api.url}dashboard/city/bs-is`,
      { params }
    );
  }
  getMarketReadinessData(ulbId: string, year: string): Observable<MarketReadinessResponse> {
    let params = new HttpParams()
      .set('ulbId', ulbId)
      .set('year', year);

    return this.http.get<MarketReadinessResponse>(
      `${environment.api.url}ledger/market-readiness-data-by-ulb`,
      { params }
    );
  }
  getMarketReadinessTable(params: any) {
    return this.http.get<any>(
      `${environment.api.url}ledger/get-all-ulbs-market-readiness`,
      { params }
    );
  }
  getUlbSlugByName(cityName: string) {
    return this.http.get<{
      ulbId: string;
      ulbName: string;
      slug: string;
    }>(
      `${environment.api.url}ledger/get-ulb-slug-by-name`,
      {
        params: {
          name: cityName
        }
      }
    );
  }

  getAllStates(): Observable<any> {
    return this.http.get<any>(`${environment.api.url}ledger/get-all-states`);
  }

  // City page: borrowings section.
  getBorrowingsData(ulbId: string = '', stateId: string = '') {
    let params = new HttpParams();
    if (ulbId) params = params.set('ulbId', ulbId);
    if (stateId) params = params.set('stateId', stateId);

    return this.http.get<{ data: BorrowingsKeys[] }>(
      `${environment.api.url}/BondIssuerItem`,
      { params }
    );
  }

  // Json file in UI.
  getCreditRatingsData() {
    return this.http.get<ICreditRatingData[]>(
      `/assets/files/credit-rating-new.json`
    );
  }
  getCreditRatings() {
    return this.http.get<ICreditRatingData[]>(
      `/assets/files/credit-rating.json`
    );
  }

  getServiceDropDown(type: string) {
    return this.http.get(environment.api.url + `state-list-of-indics?type=${type}`);
  }

  getYearListSLB() {
    return this.http.get(environment.api.url + `get-FYs-slb`);
  }
  // Get 28 Slbs data.
  fetchCitySlbChartData(
    type = 'Water Supply',
    compUlb = '',
    ulb = '',
    year = '2020-21'
  ) {
    let params = new HttpParams();
    if (type) params = params.set('type', type);
    if (compUlb) params = params.set('compUlb', compUlb);
    if (ulb) params = params.set('ulb', ulb);
    if (year) params = params.set('year', year);

    const parseYear = parseInt(year.split('-')[0], 10);
    const url = parseYear <= 2020 ?
      `${environment.api.url}indicators` :
      `${environment.api.url}dashboard/city/slb-indicators`;
    return this.http.get<{ data: ISlb[] }>(url, { params });
  }

  // Get financial indicators data.
  getFinancialIndicatorsChartData(body: IFinancialIndicatorsChart): Observable<IFinancialIndicatorRes> {
    return this.http.post<IFinancialIndicatorRes>(`${environment.api.url}dashboard/city/financial-indicators`, body);
  }

  getMarketDashboardIndicators(ulbId: string, keyType: string, years: string[]): Observable<any> {
    // Construct HttpParams dynamically
    let params = new HttpParams();
    if (keyType) {
      params = params.set('keyType', keyType);
    }
    if (ulbId) {
      params = params.set('ulbId', ulbId);
    }
    if (years && years.length > 0) {
      years.forEach(year => {
        params = params.append('years[]', year); // Append 'years[]' multiple times
      });
    }
    return this.http.get(`${environment.api.url}ledger/getCityDasboardIndicators`, { params })
  }
  getYearsDynamic(ulbId: string): Observable<any> {
    let params = new HttpParams();
    if (ulbId) {
      params = params.set('ulbId', ulbId);
    }
    return this.http.get(`${environment.api.url}ledger/getYearsDynamic`, { params })

  }
  getFaqs(ulbId: string, year: string, state: string, populationType: string): Observable<any> {
    let params = new HttpParams();
    if (state) {
      params = params.set('state', state);
    }
    if (ulbId) {
      params = params.set('ulbId', ulbId);
    }
    if (year) {
      params = params.set('year', year);
    }
    if (populationType) {
      params = params.set('populationType', populationType);
    }

    return this.http.get(`${environment.api.url}ledger/getFaqs`, { params })
  }

  getIndicatorsListCompareBy(): Observable<any> {
    return this.http.get<any>(
      `${environment.api.url}ledger/getIndicatorsNameCompareByPage`
    );
  }
  getCompareByIndicators(ulbIds: string[], years: string[], keyTypes: string[]): Observable<any> {
    const params = new HttpParams({
      fromObject: {
        ulbIds,
        years,
        keyType: keyTypes
      }
    });
    return this.http.get<any>(
      `${environment.api.url}ledger/getCompareByIndicators`,
      { params }
    );
  }
  getUlbDetailsById(slug: string): Observable<any> {
    return this.http.get<any>(
      `${environment.api.url}ledger/getUlbDetailsById`,
      { params: { slug } }
    );
  }
  // // Get state details.
  // getHomeData(): Observable<any> {
  //   return this.http.get<ExploreSectionResponse>(`${environment.api.url}report/dashboard/home-page-data`)
  //   // .pipe(
  //   //   map((response: any) => {
  //   //     const data = response.data;
  //   //     const result: { key: string; label: string; value: number }[] = [];

  //   //     for (const key in data) {
  //   //       if (Array.isArray(data[key])) continue;

  //   //       result.push({
  //   //         key,
  //   //         label: key,
  //   //         value: data[key].toLocaleString('en-IN')
  //   //       });
  //   //     }

  //   //     return result;
  //   //   })
  //   // );
  // }

  // Get state details.
  getStateDetails(params: { slug: string; year: string }): Observable<ExploreSectionResponse> {
    return this.http.get<ExploreSectionResponse>(`${environment.api.url}dashboard/state/details`, { params });
  }
  // Get state details.
  getStateGroupPopulation(params: { stateId: string; }): Observable<ExploreSectionResponse> {
    return this.http.get<ExploreSectionResponse>(`${environment.api.url}state-ulbs-grouped-by-population`, { params });
  }
  getSlbPopulation(payload: any): Observable<any> {
    return this.http.post(`${environment.api.url}state-slb`, payload);
  }
  getStatePopulation(params: any): Observable<any> {
    const options: any = {
      params,
      ...(params.csv && { responseType: 'blob' })
    };

    return this.http.get(`${environment.api.url}state-revenue-tabs`, options);
  }
  getStateRevenue(payload: any, apiEndPoint = 'state-revenue'): Observable<{ sucess: boolean, data: any }> {
    return this.http.post<any>(`${environment.api.url + apiEndPoint}`, payload);
  }

  getDashboardTabData(dashboardId: string): Observable<any> {
    return this.http.get(`${environment.api.url}dashboardHeaders/${dashboardId}`)
      .pipe(
        map((response: any) => {
          return this.formatTabs(response.data)
        })
      );
  }

  formatTabs(data: any) {
    const tabs: any[] = [];
    data.sort((a: any, b: any) => a.position - b.position).forEach((tab: any) => {
      const buttons: any[] = [];
      const subHeaders = tab.subHeaders;
      if (subHeaders && subHeaders.length > 0) {
        subHeaders.forEach((btn: any) => {
          let subButtons: any = {};
          subButtons = {
            text: btn.mainContent[0].about,
            buttons: btn.mainContent[0].btnLabels.map((subBtn: string) => ({ key: subBtn, label: subBtn }))
          };
          buttons.push({
            key: btn.name,
            label: btn.name,
            subButtons
          });
        });
      }
      tabs.push({
        name: tab.name,
        buttons: buttons,
      });
    });
    return tabs;
  }

  getDataAvailable(payload: any): Observable<any> {
    if (payload && payload.csv) return this.http.post(`${environment.api.url}data-available`, payload, { responseType: 'blob' });
    else return this.http.post(`${environment.api.url}data-available`, payload);
  }


}
